import type { IAdminCompanyRow } from '@commenda/api/admin';
import { BulkSendCompanyMessageResultItem } from '@commenda/api/chatRoom';
import { TBulkMessageJobStatus } from '@commenda/api/job';
import {
  Button,
  ColumnGroup,
  CustomColumnDef,
  DataTable,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Tooltip,
  useToggle,
} from '@commenda/kakashi';
import { useMemo } from 'react';
import { downloadFailedCompanyCsv } from './downloadFailedCompanyCsv';

interface CompanyFailedStepProps {
  failedResults: BulkSendCompanyMessageResultItem[];
  selectedCompanies: IAdminCompanyRow[];
  message: string;
  jobStatus: TBulkMessageJobStatus | null;
  onClose: () => void;
}

export const CompanyFailedStep = ({
  failedResults,
  selectedCompanies,
  message,
  jobStatus,
  onClose,
}: CompanyFailedStepProps) => {
  const hasDownloadedFailedCsv = useToggle(false);

  const failedColumns: CustomColumnDef<BulkSendCompanyMessageResultItem, unknown>[] = useMemo(() => [
    {
      id: 'company',
      accessorKey: 'companyId',
      header: 'Company',
      cell: ({ row }) => {
        const company = selectedCompanies.find((c) => c.id === row.original.companyId);
        return (
          <div>
            <p className="txt-small-medium">{company?.displayName || `Company #${row.original.companyId}`}</p>
            <p className="txt-extra-small-regular text-text-tertiary">{company?.email}</p>
          </div>
        );
      },
    },
    {
      id: 'error',
      accessorKey: 'error',
      header: 'Error',
      cell: ({ row }) => (
        <span className="text-text-error">{row.original.error}</span>
      ),
    },
  ], [selectedCompanies]);

  const failedColumnGroups: ColumnGroup<BulkSendCompanyMessageResultItem, unknown>[] = useMemo(() => [
    { id: 'main', columns: failedColumns },
  ], [failedColumns]);

  return (
    <>
      <DialogHeader className="flex flex-col gap-xs items-start">
        <DialogTitle>Partial Failure</DialogTitle>
        <DialogDescription>
          {jobStatus?.job.successCount ?? 0} message(s) sent successfully.{' '}
          {jobStatus?.job.failureCount ?? failedResults.length} message(s) failed.
          A CSV file with the failed details has been downloaded.
        </DialogDescription>
      </DialogHeader>

      <div className="px-3xl py-2xl min-w-0 overflow-y-auto flex-1">
        <div className="overflow-x-auto rounded-md border border-border-primary">
          <DataTable
            columnGroups={failedColumnGroups}
            data={failedResults}
          />
        </div>
      </div>

      <DialogFooter>
        <Tooltip content="These details are required for an engineer to debug" asChild>
          <Button
            variant="primary"
            onClick={() => {
              downloadFailedCompanyCsv(failedResults, selectedCompanies, message);
              hasDownloadedFailedCsv.on();
            }}
          >
            Download Failed Message Details
          </Button>
        </Tooltip>
        <Tooltip content="Downloading failed details is required to enable close" disabled={hasDownloadedFailedCsv.value} asChild>
          <Button variant="secondary" onClick={onClose} disabled={!hasDownloadedFailedCsv.value}>
            Close
          </Button>
        </Tooltip>
      </DialogFooter>
    </>
  );
};
